import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { WifiOff, RefreshCw, Phone, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Feb 22, 2026 — surfaces every checked-in staff whose device has stopped
// sending GPS pings for > N minutes. Motivation: AZAD's 4:30 pm excursion
// was missed on prod because Android Battery Saver killed his background
// GPS service — 0 pings reached the backend so the Unplan-Visit gates
// never fired. Admin was blind. This card makes that blindness LOUD.

function fmtClock(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function fmtGap(m) {
  if (m == null) return 'No ping today';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = Math.round(m - h * 60);
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}

export default function StaleTrackers() {
  const [minutes, setMinutes] = useState(15);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/stale-trackers?minutes=${minutes}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [minutes]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60 s while the tab is visible.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(() => { if (!document.hidden) load(); }, 60_000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const rows = data?.rows || [];
  const asOf = useMemo(() => {
    if (!data?.as_of) return '';
    try { return new Date(data.as_of).toLocaleTimeString('en-IN'); } catch { return ''; }
  }, [data]);

  return (
    <div className="space-y-4" data-testid="stale-trackers-page">
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <WifiOff size={20} className="text-red-600" />
            <h2 className="font-bold text-lg text-gray-900">Stale Trackers</h2>
            <span className="text-xs text-gray-500">Checked-in staff whose GPS has gone silent</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <label className="text-gray-600">Threshold:</label>
            <select
              value={minutes}
              onChange={(e) => setMinutes(Number(e.target.value))}
              className="border rounded-lg px-2 py-1"
              data-testid="stale-minutes-select"
            >
              {[5, 10, 15, 20, 30, 45, 60].map((v) => (
                <option key={v} value={v}>{v} min</option>
              ))}
            </select>
            <label className="flex items-center gap-1 ml-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                data-testid="stale-autorefresh"
              />
              Auto-refresh
            </label>
            <button
              onClick={load}
              disabled={loading}
              className="ml-1 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50"
              data-testid="stale-refresh"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm text-gray-700 mb-3">
          <div>Silent staff: <strong className="text-red-600">{rows.length}</strong></div>
          {asOf && <div className="text-xs text-gray-500">as of {asOf}</div>}
        </div>

        {err && (
          <div className="text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 text-sm mb-3" data-testid="stale-error">{err}</div>
        )}

        {!loading && rows.length === 0 && !err && (
          <div className="text-center py-8 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="stale-empty">
            All checked-in staff are pinging within the last {minutes} min. Nothing to worry about.
          </div>
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="stale-table">
              <thead className="text-left text-gray-600 border-b">
                <tr>
                  <th className="py-2 pr-3">Staff</th>
                  <th className="py-2 pr-3">Staff ID</th>
                  <th className="py-2 pr-3">Mobile</th>
                  <th className="py-2 pr-3">Geofence</th>
                  <th className="py-2 pr-3">Last ping</th>
                  <th className="py-2 pr-3">GPS gap</th>
                  <th className="py-2 pr-3">Checked in at</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.staff_id} className="border-b hover:bg-red-50" data-testid={`stale-row-${row.staff_id}`}>
                    <td className="py-2 pr-3 font-medium text-gray-900">{row.staff_name}</td>
                    <td className="py-2 pr-3 text-gray-700">{row.staff_code}</td>
                    <td className="py-2 pr-3">
                      {row.mobile && row.mobile !== '-' ? (
                        <a href={`tel:${row.mobile}`} className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                          <Phone size={12} /> {row.mobile}
                        </a>
                      ) : '—'}
                    </td>
                    <td className="py-2 pr-3 text-gray-700 inline-flex items-center gap-1"><MapPin size={12} /> {row.geofence_name}</td>
                    <td className="py-2 pr-3 text-gray-700">{fmtClock(row.last_ping_at)}</td>
                    <td className="py-2 pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${row.gap_minutes == null || row.gap_minutes >= 60 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                        {fmtGap(row.gap_minutes)}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-gray-700">{fmtClock(row.checkin_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3 text-xs text-gray-500">
          Tip: call the staff and ask them to open the app. On Android, most silent trackers are caused by Battery Saver / OEM App Standby suspending the background GPS service. The one-time Battery Wizard on the staff dashboard walks them through disabling it.
        </div>
      </div>
    </div>
  );
}
